import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Leaf-through reader: a book's pages in a quiet lightbox with prev/next.
const BookReader = ({ book, onClose }) => {
  const [page, setPage] = useState(0);
  const pages = book?.pages ?? [];

  const next = useCallback(() => setPage((p) => Math.min(p + 1, pages.length - 1)), [pages.length]);
  const prev = useCallback(() => setPage((p) => Math.max(p - 1, 0)), []);

  useEffect(() => {
    setPage(0);
  }, [book]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, next, prev]);

  return (
    <AnimatePresence>
      {book && (
        <motion.div
          data-testid="book-reader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="fixed inset-0 z-[80] flex flex-col items-center justify-center p-6 md:p-12"
          style={{ background: "rgba(3,6,8,0.92)", backdropFilter: "blur(10px)" }}
          onClick={onClose}
        >
          <p
            data-testid="book-reader-title"
            className="font-serif-display mb-5 text-center text-lg italic text-[#d4af37]/80"
            style={{ letterSpacing: "0.12em" }}
          >
            {book.title}
          </p>

          <div className="relative flex w-full items-center justify-center gap-4 md:gap-10">
            <button
              data-testid="book-reader-prev"
              aria-label="Previous page"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              disabled={page === 0}
              className="shrink-0 text-[#8b9a9a] transition-all duration-500 hover:text-[#d4af37] disabled:opacity-20"
            >
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
                <path d="M20 5 L9 15 L20 25" stroke="currentColor" strokeWidth="1" />
              </svg>
            </button>

            <AnimatePresence mode="wait">
              <motion.img
                key={pages[page]}
                data-testid="book-reader-page"
                src={pages[page]}
                alt={`${book.title} — page ${page + 1}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="max-h-[68vh] max-w-full object-contain"
                style={{ boxShadow: "0 30px 80px rgba(3,6,8,0.9), 0 0 0 1px rgba(212,175,55,0.2)" }}
                onClick={(e) => e.stopPropagation()}
              />
            </AnimatePresence>

            <button
              data-testid="book-reader-next"
              aria-label="Next page"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              disabled={page === pages.length - 1}
              className="shrink-0 text-[#8b9a9a] transition-all duration-500 hover:text-[#d4af37] disabled:opacity-20"
            >
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
                <path d="M10 5 L21 15 L10 25" stroke="currentColor" strokeWidth="1" />
              </svg>
            </button>
          </div>

          <p
            data-testid="book-reader-count"
            className="mt-5 text-[10px] uppercase text-[#8b9a9a]/70"
            style={{ letterSpacing: "0.35em" }}
          >
            {page + 1} / {pages.length}
          </p>

          <button
            data-testid="book-reader-close"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-6 top-6 text-[#8b9a9a] transition-colors duration-500 hover:text-[#d4af37] md:right-10 md:top-8"
          >
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
              <path d="M4 4 L22 22 M22 4 L4 22" stroke="currentColor" strokeWidth="1" />
            </svg>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookReader;
